import { Box, Card, Divider, Icon, LoadingMask, Paragraph, Popover, Switch, Text } from '@zillow/constellation-10';

import { Button } from '@zillow/constellation-10';
import { IconCloseFilled, IconFilterOutline, IconInfoFilled } from '@zillow/constellation-icons';
import { useState } from 'react';
import { MarkerIcon } from './Dots/IconMapDot';
import { styled } from '@/styled-system/jsx';

interface MapControlItem {
  title: string;
  isLoading?: boolean;
  popupText?: string;
  controls: Array<{
    backgroundColor?: string;
    iconColor?: string;
    icon: React.ReactNode;
    title: string;
    checked: boolean;
    onChange: () => void;
    items?: Array<unknown>;
  }>;
}

const SwitchItem = ({
  icon,
  backgroundColor,
  iconColor,
  title,
  checked,
  onChange,
  popupText,
}: {
  icon: React.ReactNode;
  backgroundColor?: string;
  iconColor?: string;
  title: string;
  popupText?: string;
  checked: boolean;
  onChange: () => void;
}) => (
  <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Box css={{ display: 'flex', alignItems: 'center', gap: '500' }}>
      <MarkerIcon icon={icon as React.ReactElement} backgroundColor={backgroundColor} iconColor={iconColor} />
      <Text css={{ display: 'flex', alignItems: 'center', gap: '50' }}>
        {title}
        {popupText && (
          <Popover
            header={<Text textStyle="body-sm-bold">{title}</Text>}
            closeButton={<span hidden />}
            body={<Paragraph textStyle="body-sm">{popupText}</Paragraph>}
            trigger={
              <Icon size="sm" css={{ color: 'gray.500' }}>
                <IconInfoFilled />
              </Icon>
            }
            shouldAwaitInteractionResponse
            tone="neutral"
          />
        )}
      </Text>
    </Box>
    <Switch css={{ height: 'min-content' }} size="md" checked={checked} onChange={onChange} />
  </Box>
);

const StyledDivider = styled(Divider, {
  base: {
    marginBlock: '400',
  },
});

export const MapControls = ({ sections }: { sections: Array<MapControlItem> }) => {
  const [areFiltersOpen, setAreFiltersOpen] = useState(true);

  return (
    <Box css={{ position: 'absolute', top: '400', left: '400', width: '350px' }}>
      <Button
        tone="neutral"
        emphasis="outlined"
        icon={areFiltersOpen ? <IconCloseFilled /> : <IconFilterOutline />}
        iconPosition="start"
        onClick={() => setAreFiltersOpen(!areFiltersOpen)}
      >
        Filters
      </Button>
      {areFiltersOpen && (
        <Card elevated css={{ marginBlockStart: '300' }}>
          {sections.map((item, index) => {
            const isLoading = item.isLoading === undefined ? false : item.isLoading;

            const visibleControls = item.controls.filter(
              (control) => control.items === undefined || control.items.length > 0 || isLoading,
            );

            if (visibleControls.length === 0) return null;

            return (
              <Box key={item.title}>
                {index > 0 && <StyledDivider />}
                <LoadingMask
                  loading={isLoading}
                  iconSize="sm"
                  css={{ display: 'flex', flexDirection: 'column', gap: '100' }}
                >
                  <Paragraph textStyle="body-bold" css={{ display: 'flex', alignItems: 'center', gap: '50' }}>
                    {item.title}
                    {item.popupText && (
                      <Popover
                        header={<Text textStyle="body-sm-bold">{item.title}</Text>}
                        closeButton={<span hidden />}
                        body={<Paragraph textStyle="body-sm">{item.popupText}</Paragraph>}
                        trigger={
                          <Icon size="sm" css={{ color: 'gray.500' }}>
                            <IconInfoFilled />
                          </Icon>
                        }
                        shouldAwaitInteractionResponse
                        tone="neutral"
                      />
                    )}
                  </Paragraph>
                  <Box css={{ display: 'flex', flexDirection: 'column', gap: '100' }}>
                    {visibleControls.map((control) => (
                      <SwitchItem key={control.title} {...control} />
                    ))}
                  </Box>
                </LoadingMask>
              </Box>
            );
          })}
        </Card>
      )}
    </Box>
  );
};
